import { z } from 'zod';
import { useMutation } from '@applyboard/ui-utils';
import { FxRates } from './fxRates';

const DEFAULT_ELIGIBILITY_CURRENCY = 'USD';

//TODO: Find a better way to represent this
const LOAN_ELIGIBILITY_DATA: Record<string, Record<string, Record<string, number>>> = {
  ['global']: {
    INR: {
      median: 3_200_000,
      p90: 5_906_280,
    },
    USD: {
      median: 38_554,
      p90: 71160,
    },
  },
  ['Information Technology']: {
    INR: {
      median: 3_410_000,
      p90: 6_285_500,
    },
    USD: {
      median: 41_084,
      p90: 75_728,
    },
  },
  ['Healthcare / Nursing']: {
    INR: {
      median: 3_500_000,
      p90: 5_100_000,
    },
    USD: {
      median: 42_168,
      p90: 61_445,
    },
  },
  ['Engineering']: {
    INR: {
      median: 3_527_500,
      p90: 6_068_130,
    },
    USD: {
      median: 42_500,
      p90: 73_110,
    },
  },
  ['Education']: {
    INR: {
      median: 2_772_200,
      p90: 4_449_200,
    },
    USD: {
      median: 33_400,
      p90: 53_604,
    },
  },
  ['Computer Science']: {
    INR: {
      median: 4_000_000,
      p90: 6_849_160,
    },
    USD: {
      median: 48_192,
      p90: 82_520,
    },
  },
  ['Business Administration']: {
    INR: {
      median: 3_000_000,
      p90: 5_745_260,
    },
    USD: {
      median: 36_144,
      p90: 69_220,
    },
  },
  ['Biotechnology']: {
    INR: {
      median: 3_100_000,
      p90: 5_471_360,
    },
    USD: {
      median: 37_349,
      p90: 65_920,
    },
  },
  ['Accounting And Finance']: {
    INR: {
      median: 2_500_000,
      p90: 5_000_000,
    },
    USD: {
      median: 30_120,
      p90: 60_240,
    },
  },
};

const LoanEligibilityInputSchema = z.object({
  nationality: z.string(),
  school: z.string().optional(),
  fieldOfStudy: z.string().optional(),
  loanAmount: z.string(),
  loanAmountCurrency: z.string(),
  tuitionAmount: z.string().optional(),
  tuitionAmountCurrency: z.string().optional(),
  employmentStatus: z.string().optional(),
  annualIncome: z.string().optional(),
  annualIncomeCurrency: z.string().optional(),
  hasCosigner: z.boolean().optional(),
});

export type LoanEligibilityInput = z.infer<typeof LoanEligibilityInputSchema>;

export type Eligibility = 'HIGH' | 'MEDIUM' | 'LOW';

/**
 * FxRates is based on USD, to convert an amount from a non-USD currency to another non-USD currency
 * you need to first convert the amount to USD using the source currency's exchange rate,
 * and then convert the USD amount to the target currency using the target currency's exchange rate.
 */
function convertToCurrency(
  originalAmount: number,
  originalCurrency: string,
  targetCurrency: string,
  fxRates: FxRates,
): { amount: number; currency: string } {
  if (originalCurrency === targetCurrency) {
    return { amount: originalAmount, currency: targetCurrency };
  }

  const supportedCurrencies = [...Object.keys(fxRates.rates), fxRates.sourceCurrency];
  if (!supportedCurrencies.includes(originalCurrency)) {
    throw new Error('Exchange rate not found for original currency');
  }

  const rate = originalCurrency === fxRates.sourceCurrency ? 1 : fxRates.rates[originalCurrency];
  const amountInFxRateSourceCurrency = originalAmount / rate;
  if (targetCurrency === fxRates.sourceCurrency) {
    return { amount: amountInFxRateSourceCurrency, currency: 'USD' };
  }

  if (targetCurrency in fxRates.rates) {
    const exchangeRate = fxRates.rates[targetCurrency];

    return {
      amount: amountInFxRateSourceCurrency * exchangeRate,
      currency: targetCurrency,
    };
  }

  throw new Error('Exchange rate not found for target currency');
}

function calculateEligibility(input: LoanEligibilityInput, fxRates: FxRates): Eligibility {
  const eligibilityCurrency = input.nationality === 'IN' ? 'INR' : DEFAULT_ELIGIBILITY_CURRENCY;
  const eligibilityData =
    LOAN_ELIGIBILITY_DATA[input.fieldOfStudy ?? '']?.[eligibilityCurrency] ||
    LOAN_ELIGIBILITY_DATA.global[eligibilityCurrency];
  const loanAmountInInt = parseInt(input.loanAmount);

  if (isNaN(loanAmountInInt)) {
    throw new Error(`Invalid loan amount ${input.loanAmount}`);
  }

  const convertedLoanAmount = convertToCurrency(
    loanAmountInInt,
    input.loanAmountCurrency,
    eligibilityCurrency,
    fxRates,
  );

  if (convertedLoanAmount.amount <= eligibilityData.median) {
    return 'HIGH';
  } else if (convertedLoanAmount.amount > eligibilityData.p90) {
    return 'LOW';
  }

  return 'MEDIUM';
}

export function useLoanEligibilityChecker(fxRates: FxRates) {
  const { mutate, mutateAsync, isLoading, isError } = useMutation(
    async (eligibilityCriteria: LoanEligibilityInput) =>
      calculateEligibility(eligibilityCriteria, fxRates),
  );

  const getEligibility = (
    params: LoanEligibilityInput,
    onSuccess?: (result: Eligibility) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: async (response: Eligibility) => onSuccess?.(response),
      onError: (error: unknown) => onError?.(error),
    });
  };

  const getEligibilityAsync = (params: LoanEligibilityInput) => mutateAsync(params);

  return {
    getEligibility,
    getEligibilityAsync,
    isGetEligibilityLoading: isLoading,
    isGetEligibilityError: isError,
  };
}
